/**
 * LE PILOTAGE — lot 148, 02/11/2026.
 *
 * L'entrée « Journées » devient « Pilotage », avec deux onglets : le TABLEAU
 * DE BORD (chiffres clés et graphiques) et le POINTAGE (l'écran des journées,
 * inchangé). Maquette validée le 02/11 (`docs/maquettes/pilotage.html`).
 *
 * LES GRAPHIQUES SONT DESSINÉS À LA MAIN, en SVG : aucune bibliothèque n'est
 * installée dans l'interface, et quatre graphiques simples n'en justifient pas
 * une. Le survol d'une barre ou d'un point montre son détail (`<title>`).
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { EcranJournees } from './EcranJournees.js';
import { aujourdhui } from './ot.js';
import {
  clientsResumes,
  coutAuKm,
  eurosRonds,
  jourCourt,
  kmDits,
  lirePilotage,
  periodeDe,
  plafondRond,
  pourcentage,
  raccourciDe,
  type Periode,
  type PilotageLu,
  type Raccourci,
} from './pilotage.js';

export type OngletPilotage = 'tableau' | 'pointage';

export type EtatPilotage =
  | { readonly quoi: 'chargement' }
  | { readonly quoi: 'pret'; readonly lu: PilotageLu }
  | { readonly quoi: 'panne' };

// ---------------------------------------------------------------------------
// Les graphiques
// ---------------------------------------------------------------------------

const L = 600;
const H = 200;
const G = 52; // marge gauche, pour les graduations
const BAS = 176; // la ligne de base

function Graduations({ max, format }: { readonly max: number; readonly format: (v: number) => string }) {
  return (
    <>
      {[0, 1, 2, 3, 4].map((i) => {
        const v = (max * i) / 4;
        const y = BAS - (v / max) * (BAS - 14);
        return (
          <g key={i}>
            <line x1={G} x2={L - 4} y1={y} y2={y} className="pil-grille" />
            <text x={G - 6} y={y + 4} textAnchor="end" className="pil-graduation">
              {format(v)}
            </text>
          </g>
        );
      })}
    </>
  );
}

function Abscisses({ jours }: { readonly jours: readonly string[] }) {
  const w = (L - 4 - G) / Math.max(1, jours.length);
  const tous = Math.max(1, Math.ceil(jours.length / 7));
  return (
    <>
      {jours.map((j, i) =>
        i % tous === 0 ? (
          <text key={j} x={G + i * w + w / 2} y={BAS + 18} textAnchor="middle" className="pil-graduation">
            {jourCourt(j)}
          </text>
        ) : null,
      )}
    </>
  );
}

export function GraphiqueKm({ lu }: { readonly lu: PilotageLu }) {
  const jours = lu.par_jour.filter((j) => j.km > 0);
  const max = plafondRond(Math.max(0, ...jours.map((j) => j.km)));
  const w = (L - 4 - G) / Math.max(1, jours.length);
  return (
    <svg viewBox={`0 0 ${String(L)} ${String(H)}`} width="100%" role="img" aria-label="Km par jour">
      <Graduations max={max} format={(v) => kmDits(Math.round(v))} />
      {jours.map((j, i) => {
        const x = G + i * w + 3;
        const bw = Math.max(2, w - 6);
        const hc = ((j.km - j.km_vide) / max) * (BAS - 14);
        const hv = (j.km_vide / max) * (BAS - 14);
        return (
          <g key={j.jour}>
            <title>{`${jourCourt(j.jour)} : ${kmDits(j.km)} km, dont ${kmDits(j.km_vide)} à vide`}</title>
            <rect x={x} y={BAS - hc} width={bw} height={hc} className="pil-charge" />
            {hv > 2 && <rect x={x} y={BAS - hc - hv} width={bw} height={hv - 2} rx={3} className="pil-vide" />}
          </g>
        );
      })}
      <Abscisses jours={jours.map((j) => j.jour)} />
    </svg>
  );
}

export function GraphiqueCaCout({ lu }: { readonly lu: PilotageLu }) {
  const jours = lu.par_jour;
  const max = plafondRond(Math.max(0, ...jours.flatMap((j) => [j.ca_centimes, j.cout_centimes])));
  const w = (L - 4 - G) / Math.max(1, jours.length);
  const point = (i: number, v: number) => `${String(G + i * w + w / 2)},${String(BAS - (v / max) * (BAS - 14))}`;
  return (
    <svg viewBox={`0 0 ${String(L)} ${String(H)}`} width="100%" role="img" aria-label="Chiffre d’affaires et coût par jour">
      <Graduations max={max} format={(v) => `${kmDits(Math.round(v / 100_000))} k€`} />
      {jours.length > 1 && (
        <>
          <polyline points={jours.map((j, i) => point(i, j.cout_centimes)).join(' ')} className="pil-ligne-cout" />
          <polyline points={jours.map((j, i) => point(i, j.ca_centimes)).join(' ')} className="pil-ligne-ca" />
        </>
      )}
      {jours.map((j, i) => {
        const [cx, cyCa] = point(i, j.ca_centimes).split(',').map(Number);
        const [, cyCout] = point(i, j.cout_centimes).split(',').map(Number);
        return (
          <g key={j.jour}>
            <title>{`${jourCourt(j.jour)} : CA ${eurosRonds(j.ca_centimes)}, coût ${eurosRonds(j.cout_centimes)}, marge ${eurosRonds(j.ca_centimes - j.cout_centimes)}`}</title>
            {/* LA ZONE DE SURVOL est la colonne entière, plus large que les points. */}
            <rect x={G + i * w} y={10} width={w} height={BAS - 10} className="pil-survol" />
            <circle cx={cx} cy={cyCout} r={3.5} className="pil-point-cout" />
            <circle cx={cx} cy={cyCa} r={3.5} className="pil-point-ca" />
          </g>
        );
      })}
      <Abscisses jours={jours.map((j) => j.jour)} />
    </svg>
  );
}

function Rangs({
  lignes,
  avecDetail,
}: {
  readonly lignes: readonly {
    readonly cle: string;
    readonly nom: string;
    readonly ca_centimes: number;
    readonly km?: number;
    readonly marge_centimes?: number;
  }[];
  readonly avecDetail: boolean;
}) {
  const max = Math.max(1, ...lignes.map((l) => l.ca_centimes));
  return (
    <div className={avecDetail ? 'pil-rangs' : 'pil-rangs pil-rangs-ca'}>
      <div className="pil-rang pil-rang-tete">
        <span />
        <span />
        <span className="pil-chiffre">CA</span>
        {avecDetail && <span className="pil-chiffre">km</span>}
        {avecDetail && <span className="pil-chiffre">marge</span>}
      </div>
      {lignes.map((l) => (
        <div className="pil-rang" key={l.cle}>
          <span className="pil-nom">{l.nom}</span>
          <span className="pil-barre">
            <i style={{ width: `${String(Math.max(0, (l.ca_centimes / max) * 100))}%` }} />
          </span>
          <span className="pil-chiffre">{eurosRonds(l.ca_centimes)}</span>
          {avecDetail && <span className="pil-chiffre">{kmDits(l.km ?? 0)}</span>}
          {avecDetail && (
            <span className={(l.marge_centimes ?? 0) < 0 ? 'pil-chiffre pil-marge pil-perte' : 'pil-chiffre pil-marge'}>
              {eurosRonds(l.marge_centimes ?? 0)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Le dessin
// ---------------------------------------------------------------------------

export function TableauDeBord({
  periode,
  raccourci,
  etat,
  quandRaccourci,
  quandPeriode,
}: {
  readonly periode: Periode;
  readonly raccourci: Raccourci | null;
  readonly etat: EtatPilotage;
  readonly quandRaccourci: (r: Raccourci) => void;
  readonly quandPeriode: (p: Periode) => void;
}) {
  const lu = etat.quoi === 'pret' ? etat.lu : null;
  const t = lu?.totaux;
  return (
    <div className="pil-tableau">
      <div className="pil-periode">
        {(
          [
            ['semaine', 'Semaine'],
            ['mois', 'Mois'],
            ['mois_precedent', 'Mois précédent'],
          ] as const
        ).map(([r, libelle]) => (
          <button
            key={r}
            type="button"
            className={raccourci === r ? 'pil-pilule actif' : 'pil-pilule'}
            onClick={() => quandRaccourci(r)}
          >
            {libelle}
          </button>
        ))}
        <input
          className="champ pil-date"
          type="date"
          aria-label="Du"
          value={periode.du}
          onChange={(e) => {
            if (e.target.value !== '') quandPeriode({ ...periode, du: e.target.value });
          }}
        />
        <span aria-hidden="true">→</span>
        <input
          className="champ pil-date"
          type="date"
          aria-label="Au"
          value={periode.au}
          onChange={(e) => {
            if (e.target.value !== '') quandPeriode({ ...periode, au: e.target.value });
          }}
        />
        {lu !== null && (
          <span className="pil-compte">
            {lu.journees_comptees} journée{lu.journees_comptees > 1 ? 's' : ''} clôturée
            {lu.journees_comptees > 1 ? 's' : ''}
            {lu.incompletes > 0 &&
              ` · ${String(lu.incompletes)} non clôturée${lu.incompletes > 1 ? 's' : ''} ou incomplète${lu.incompletes > 1 ? 's' : ''}, non comptée${lu.incompletes > 1 ? 's' : ''}`}
          </span>
        )}
      </div>

      {etat.quoi === 'chargement' && <p className="attente">Lecture…</p>}
      {etat.quoi === 'panne' && <p className="bandeau-refus">Le tableau de bord n’a pas pu être lu.</p>}

      {lu !== null && t !== undefined && (
        <>
          <div className="pil-tuiles">
            <div className="pil-tuile">
              <b>{eurosRonds(t.ca_centimes)}</b>
              <span>chiffre d’affaires</span>
            </div>
            <div className="pil-tuile">
              <b className={t.marge_centimes < 0 ? 'pil-perte' : ''}>
                {eurosRonds(t.marge_centimes)} · {pourcentage(t.marge_centimes, t.ca_centimes)}
              </b>
              <span>marge</span>
            </div>
            <div className="pil-tuile">
              <b>{kmDits(t.km)}</b>
              <span>km roulés</span>
            </div>
            <div className="pil-tuile">
              <b>{pourcentage(t.km_vide, t.km)}</b>
              <span>km à vide</span>
            </div>
            <div className="pil-tuile">
              <b>{coutAuKm(t.cout_centimes, t.km)}</b>
              <span>coût au km</span>
            </div>
          </div>

          {lu.journees_comptees === 0 ? (
            <p className="pil-vide-msg">Aucune journée pointée sur cette période.</p>
          ) : (
            <div className="pil-grille-cartes">
              <section className="pil-carte">
                <h2>Km par jour</h2>
                <div className="pil-legende">
                  <span><i className="pil-charge-pastille" />chargés</span>
                  <span><i className="pil-vide-pastille" />à vide</span>
                </div>
                <GraphiqueKm lu={lu} />
              </section>
              <section className="pil-carte">
                <h2>Chiffre d’affaires et coût par jour</h2>
                <div className="pil-legende">
                  <span><i className="pil-charge-pastille" />CA</span>
                  <span><i className="pil-cout-pastille" />coût</span>
                </div>
                <GraphiqueCaCout lu={lu} />
              </section>
              <section className="pil-carte">
                <h2>Par chauffeur</h2>
                <Rangs
                  avecDetail
                  lignes={lu.par_chauffeur.map((c) => ({
                    cle: c.chauffeur_id ?? 'sans',
                    nom: c.nom,
                    ca_centimes: c.ca_centimes,
                    km: c.km,
                    marge_centimes: c.marge_centimes,
                  }))}
                />
              </section>
              <section className="pil-carte">
                <h2>Par client</h2>
                <Rangs
                  avecDetail={false}
                  lignes={clientsResumes(lu.par_client).map((c) => ({
                    cle: c.nom,
                    nom: c.nom,
                    ca_centimes: c.ca_centimes,
                  }))}
                />
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function VuePilotage({
  onglet,
  quandOnglet,
  tableau,
  pointage,
}: {
  readonly onglet: OngletPilotage;
  readonly quandOnglet: (o: OngletPilotage) => void;
  readonly tableau: ReactNode;
  readonly pointage: ReactNode;
}) {
  return (
    <div className="pil-ecran">
      <div className="ac-onglets pil-onglets" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={onglet === 'tableau'}
          className={onglet === 'tableau' ? 'ac-onglet actif' : 'ac-onglet'}
          onClick={() => quandOnglet('tableau')}
        >
          Tableau de bord
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={onglet === 'pointage'}
          className={onglet === 'pointage' ? 'ac-onglet actif' : 'ac-onglet'}
          onClick={() => quandOnglet('pointage')}
        >
          Pointage
        </button>
      </div>
      {onglet === 'tableau' ? tableau : pointage}
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'écran
// ---------------------------------------------------------------------------

export function EcranPilotage({
  jeton,
  quandAccesPerdu,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}) {
  const [onglet, setOnglet] = useState<OngletPilotage>('tableau');
  const [periode, setPeriode] = useState<Periode>(() => periodeDe('mois', aujourdhui()));
  const [etat, setEtat] = useState<EtatPilotage>({ quoi: 'chargement' });

  const lire = useCallback(
    async (p: Periode, abandonne: () => boolean) => {
      setEtat({ quoi: 'chargement' });
      const j = await jeton();
      if (j === null) {
        quandAccesPerdu();
        return;
      }
      if (p.au < p.du) {
        setEtat({ quoi: 'panne' });
        return;
      }
      const r = await lirePilotage(j, p.du, p.au);
      if (abandonne()) return;
      if (r.etat !== 'ok') {
        if (r.cause === 'acces') quandAccesPerdu();
        setEtat({ quoi: 'panne' });
        return;
      }
      setEtat({ quoi: 'pret', lu: r.valeur });
    },
    [jeton, quandAccesPerdu],
  );

  useEffect(() => {
    if (onglet !== 'tableau') return;
    let abandonne = false;
    void lire(periode, () => abandonne);
    return () => {
      abandonne = true;
    };
  }, [onglet, periode, lire]);

  return (
    <VuePilotage
      onglet={onglet}
      quandOnglet={setOnglet}
      tableau={
        <TableauDeBord
          periode={periode}
          raccourci={raccourciDe(periode, aujourdhui())}
          etat={etat}
          quandRaccourci={(r) => setPeriode(periodeDe(r, aujourdhui()))}
          quandPeriode={setPeriode}
        />
      }
      pointage={<EcranJournees jeton={jeton} quandAccesPerdu={quandAccesPerdu} />}
    />
  );
}
